import { queueCartMutation, requestShopifyJSON } from 'specht/theme';

(() => {
  'use strict';
  if (customElements.get('specht-cart-benefit')) return;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('#cart-drawer [data-specht-quantity-step]');
    if (!button) return;
    const input = button.closest('line-item-quantity')?.querySelector('[data-line-key]');
    if (!input) return;
    const previous = Number(input.value);
    const next = Math.max(Number(input.min || 1), Math.min(Number(input.max || Number.MAX_SAFE_INTEGER), previous + Number(button.dataset.spechtQuantityStep)));
    if (next === previous) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  class SpechtCartBenefit extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.root = window.Shopify?.routes?.root || '/';
      this.cart = JSON.parse(this.querySelector('[data-initial-cart]').textContent);
      this.formatter = new Intl.NumberFormat(this.dataset.locale || 'pt-BR', { style: 'currency', currency: this.dataset.currency || 'BRL' });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-benefit-apply]')) this.updateDiscount(false);
        if (event.target.closest('[data-benefit-remove]')) this.updateDiscount(true);
      }, { signal: this.abort.signal });
      document.addEventListener('cart:change', (event) => {
        if (event.detail?.cart) {
          this.cart = event.detail.baseEvent === 'variant:add' && event.detail.drawerContent
            ? event.detail.cart : this.cartWithSectionMetadata(event.detail.cart);
          queueMicrotask(() => { if (this.isConnected) this.render(); });
        }
      }, { signal: this.abort.signal });
      this.render();
    }
    disconnectedCallback() {
      this.abort?.abort();
    }
    money(cents) { return this.formatter.format(cents / 100); }
    cartWithSectionMetadata(cart) {
      const html = cart.sections?.[this.dataset.sectionId];
      if (!html) return cart;
      try {
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const seed = fragment.querySelector('specht-cart-benefit [data-initial-cart]');
        if (!seed) return cart;
        const rendered = JSON.parse(seed.textContent);
        if (Number(rendered.total_price) !== Number(cart.total_price) || Number(rendered.item_count) !== Number(cart.item_count)) return cart;
        return { ...cart, cart_level_discount_applications: rendered.cart_level_discount_applications, items: rendered.items };
      } catch (e) { return cart; }
    }
    allocations() {
      return [
        ...(this.cart.cart_level_discount_applications || []).map((application) => ({ application, amount: application.total_allocated_amount })),
        ...(this.cart.items || []).flatMap((item) => (item.line_level_discount_allocations || []).map((allocation) => ({ application: allocation.discount_application, amount: allocation.amount })))
      ];
    }
    discountCodes() {
      if (Array.isArray(this.cart.discount_codes)) {
        return this.cart.discount_codes.map(({ code }) => code).filter(Boolean);
      }
      return [...new Set(this.allocations().filter(({ application }) => application.type === 'discount_code').map(({ application }) => application.title))];
    }
    render() {
      const code = this.dataset.benefitCode.toUpperCase();
      const matches = this.allocations().filter(({ application }) => String(application.title).toUpperCase() === code);
      const applied = matches.length > 0;
      this.querySelector('[data-benefit-applied]').hidden = !applied;
      this.querySelector('[data-benefit-apply]').hidden = applied;
      this.querySelector('[data-benefit-remove]').hidden = !applied;
      const saving = this.querySelector('[data-benefit-saving]');
      const saved = matches.reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0);
      saving.hidden = !applied || saved <= 0;
      saving.textContent = saving.dataset.template.replace('@amount@', this.money(saved));
      this.renderTotals();
    }
    renderTotals() {
      const drawer = this.closest('cart-drawer');
      const footer = drawer?.querySelector('[slot="footer"]');
      if (!footer) return;
      const total = Number(this.cart.total_price);
      const original = Number(this.cart.original_total_price);
      const discount = footer.querySelector('[data-specht-discount]');
      const savings = Math.max(0, original - total);
      discount.hidden = savings === 0;
      discount.querySelector('[data-specht-discount-value]').textContent = '− ' + this.money(savings);
      const discountLabel = discount.querySelector('[data-specht-discount-label]');
      if (discountLabel) {
        const applications = this.allocations();
        const onlyWelcome = applications.length > 0 && applications.every(({ application }) => String(application.title).toUpperCase() === this.dataset.benefitCode.toUpperCase());
        if (!discountLabel.dataset.welcome) discountLabel.dataset.welcome = discountLabel.textContent;
        discountLabel.textContent = onlyWelcome ? discountLabel.dataset.welcome : discountLabel.dataset.generic;
      }

      const label = footer.querySelector('[data-specht-total-label]');
      if (label) label.textContent = label.dataset.products;
      footer.querySelector('[data-specht-total]').textContent = this.money(total);
      const pix = footer.querySelector('[data-specht-pix]');
      if (pix) {
        const percent = Number(this.dataset.pixPercent);
        const hasPix = this.allocations().some(({ application }) => /pix/i.test(String(application.title)));
        const pixMerchandise = hasPix ? total : total - Math.round(total * percent / 100);
        const parts = pix.dataset.template.split('@amount@');
        const amount = document.createElement('strong');
        amount.textContent = this.money(pixMerchandise);
        pix.replaceChildren(document.createTextNode(parts[0]), amount, document.createTextNode(parts[1] || ''));
      }
    }
    async updateDiscount(remove) {
      const code = this.dataset.benefitCode.trim();
      const drawer = this.closest('cart-drawer');
      if (this.discountBusy || !code) return;
      this.discountBusy = true;
      const status = this.querySelector('[data-benefit-status]');
      this.setAttribute('aria-busy', 'true');
      status.textContent = '';
      try {
        if (drawer && !customElements.get('cart-drawer')) throw new Error('Cart drawer unavailable');
        await queueCartMutation(async () => {
          this.cart = await requestShopifyJSON(this.root + 'cart.js');
          const codes = this.discountCodes().filter((existing) => existing.toUpperCase() !== code.toUpperCase());
          if (!remove) codes.push(code);
          const sectionId = this.dataset.sectionId;
          const sectionsToBundle = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
          );
          if (sectionId && !sectionsToBundle.includes(sectionId)) {
            sectionsToBundle.push(sectionId);
          }
          const cart = await requestShopifyJSON(this.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              discount: codes.join(','),
              sections: sectionsToBundle,
              sections_url: window.location.pathname + window.location.search
            })
          });
          this.cart = this.cartWithSectionMetadata(cart);
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', { bubbles: true, detail: { cart, baseEvent: 'specht:discount' } })
          );
          this.render();
          if (
            !remove &&
            !this.allocations().some(({ application }) => String(application.title).toUpperCase() === code.toUpperCase())
          ) {
            const error = new Error('Cart discount is not applicable');
            error.cartMessage = status.dataset.error;
            throw error;
          }
        });
      } catch (e) {
        status.textContent = e.cartMessage || status.dataset.refreshError;
        if (!e.cartMessage) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      } finally {
        this.discountBusy = false;
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('specht-cart-benefit', SpechtCartBenefit);
})();
